import React from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import IOSTabBar from "@/components/IOSTabBar";
import useIsIPhone from "@/hooks/useIsIPhone";
import { useAuth } from "@/context/AuthContext";
import { Lock } from "lucide-react";

// Rotas que uma CONTA DE EQUIPE (vendedor/bko/coordenador, is_team_member
// === true) pode acessar. Fora dessa lista, mesmo digitando a URL direto,
// cai na tela de "Acesso Negado" — não é só esconder o link no sidebar.
//
// IMPORTANTE: essa regra NUNCA se aplica ao dono da conta (is_team_member
// é false pra quem criou a conta principal) — só afeta membros criados via
// "Adicionar membro" no Pipeline de Empresa.
const TEAM_MEMBER_ALLOWED_PATHS = new Set(["/home", "/empresa", "/agentes-vivo"]);

// BKO e vendedor ganham acesso extra ao WhatsAppPage — vendedor pra
// conectar o próprio número e responder as notificações de correção; BKO
// porque é literalmente quem trabalha o dia a dia ali (recebe pedido de
// parceiro, consulta cobertura, sobe documento etc). O WhatsAppPage em si
// já se enxuga sozinho pra esses papéis, escondendo bot/prospecção/disparo.
const TEAM_ROLES_COM_WHATSAPP = new Set(["vendedor", "bko"]);
const VENDEDOR_EXTRA_PATHS = new Set(["/whatsapp"]);

// Papéis "de agência" — acesso total à ferramenta, como se fossem o dono
// da conta (todas as páginas, sem restrição de rota). Só o admin da
// plataforma pode criar/promover alguém pra um desses papéis (ver
// _require_role_permitido_self_service no backend) — aqui no front é só a
// liberação de rota mesmo, a trava de quem PODE virar isso já é no back.
const PAPEIS_DE_AGENCIA = new Set(["coordenador", "gerente", "admin_company"]);

function AccessDeniedPage() {
  return (
    <div style={{ maxWidth: 520, margin: "60px auto", padding: "0 20px" }}>
      <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 14, padding: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <Lock size={20} color="#dc2626" />
          <h2 style={{ fontSize: 17, fontWeight: 800, color: "#7f1d1d", margin: 0 }}>Acesso não autorizado</h2>
        </div>
        <p style={{ fontSize: 13.5, color: "#7f1d1d", marginBottom: 12 }}>
          Sua conta não tem permissão pra acessar essa funcionalidade.
        </p>
        <p style={{ fontSize: 13.5, color: "#7f1d1d" }}>
          Fale com o responsável pela sua empresa se precisar de acesso a mais recursos.
        </p>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user } = useAuth();
  const location = useLocation();
  const isIPhone = useIsIPhone();

  // Papel de agência = acesso total, igual dono da conta — não entra na
  // restrição de rota nenhuma, mesmo sendo tecnicamente is_team_member.
  const isRestrictedTeamMember = !!user?.is_team_member && !PAPEIS_DE_AGENCIA.has(user?.team_role);
  const pathAllowed =
    TEAM_MEMBER_ALLOWED_PATHS.has(location.pathname) ||
    (TEAM_ROLES_COM_WHATSAPP.has(user?.team_role) && VENDEDOR_EXTRA_PATHS.has(location.pathname));

  return (
    <div className={"min-h-screen flex bg-zinc-50" + (isIPhone ? " ios-layout" : "")}>
      {/* No iPhone a navegação vira a tab bar de baixo (IOSTabBar); no
          Android e PC continua a sidebar de sempre, sem nenhuma mudança. */}
      {!isIPhone && <Sidebar />}
      <main className={"flex-1 min-w-0 bg-white" + (isIPhone ? " ios-main" : "")}>
        {isRestrictedTeamMember && !pathAllowed ? <AccessDeniedPage /> : <Outlet />}
      </main>
      {isIPhone && <IOSTabBar />}
    </div>
  );
}
